import { Alert } from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { ChatStatus, isChatTextContent } from "@/shared/domain/chat/ChatRecord";
import { useCancelGeneration } from "@/shared/domain/chat/hooks/useCancelGeneration";
import { useChat } from "@/shared/domain/chat/hooks/useChat";
import { useSendMessage } from "@/shared/domain/chat/hooks/useSendMessage";
import { useStartChat } from "@/shared/domain/chat/hooks/useStartChat";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { useAIChat } from "./AIChatContext";
import styles from "./Chat.module.css";
import { ChatByIdView } from "./ChatByIdView";
import { ChatInputBox } from "./ChatInputBox";
import { NewChatView } from "./NewChatView";

/**
 * Chat component orchestrates all data fetching and mutations.
 * It conditionally renders NewChat vs ChatMessages based on whether chatId exists.
 * ChatInput is rendered as a pure form component with callbacks.
 */
export const Chat: React.FC = () => {
  const orgId = useCurrentOrgId();
  const { currentChatId, setCurrentChatId, context } = useAIChat();

  // Data fetching
  const chatQuery = useChat(currentChatId, {
    orgId,
    loadMessages: true,
  });
  const chat = chatQuery.data;

  // Mutations
  const startChatMutation = useStartChat();
  const sendMessageMutation = useSendMessage();
  const cancelGenerationMutation = useCancelGeneration();

  // Cancellation pending state - cleared when backend confirms cancellation
  const [isCancelling, setIsCancelling] = React.useState(false);
  const isSubmittingRef = React.useRef(false);

  // Clear cancellation state when chat status changes to USER_TURN
  React.useEffect(() => {
    if (chat?.status === ChatStatus.USER_TURN) {
      setIsCancelling(false);
    }
  }, [chat?.status]);

  // Derived state
  const loading =
    chatQuery.data !== undefined &&
    chatQuery.data?.status !== ChatStatus.USER_TURN;

  // Mutation handlers
  const handleStartChat = async (message: string) => {
    const chatRecord = await startChatMutation.mutateAsync({
      message,
      orgId,
      context,
    });
    setCurrentChatId(chatRecord.chat_id);
    return chatRecord;
  };

  const handleSendMessage = async (message: string) => {
    if (!currentChatId) {
      return;
    }

    await sendMessageMutation.mutateAsync({
      chatId: currentChatId,
      message: message,
      orgId,
      context,
    });
  };

  // Callbacks for ChatInput
  const handleSubmit = async (message: string): Promise<void> => {
    const shouldSendMessage =
      currentChatId !== undefined && !sendMessageMutation.isPending;
    const shouldStartChat =
      currentChatId === undefined &&
      message.trim() !== "" &&
      !startChatMutation.isPending;

    if (isSubmittingRef.current || (!shouldSendMessage && !shouldStartChat)) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      if (shouldSendMessage) {
        await handleSendMessage(message);
      } else {
        await handleStartChat(message);
      }
    } catch (err) {
      ErrorMonitoringService.captureError(err);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleExampleClick = async (question: string): Promise<void> => {
    if (!startChatMutation.isPending) {
      await handleStartChat(question);
    }
  };

  const handleCancel = async () => {
    // Reset mutations to cancel any pending requests
    startChatMutation.reset();
    sendMessageMutation.reset();

    // If we have an active chat that's generating, request cancellation from the backend
    if (currentChatId && chat?.status !== ChatStatus.USER_TURN) {
      setIsCancelling(true);
      try {
        await cancelGenerationMutation.mutateAsync({ chatId: currentChatId });
      } catch (err) {
        setIsCancelling(false);
        ErrorMonitoringService.captureError(err);
      }
    }
  };

  const handleRetry = React.useCallback(
    (messageIndex: number) => {
      if (!chat) {
        return;
      }

      const message = chat.messages[messageIndex];
      const textContent = message?.content.find(isChatTextContent);

      if (textContent === undefined) {
        return;
      }

      sendMessageMutation.reset();
      void sendMessageMutation.retry(chat.chat_id, textContent.text);
    },
    [chat, sendMessageMutation],
  );

  const sending = startChatMutation.isPending || sendMessageMutation.isPending;
  const isGenerating = loading;

  return (
    <>
      <div className={styles.content}>
        {/* Error Alert */}
        {startChatMutation.isError && (
          <Alert
            severity="error"
            onClose={() => {
              startChatMutation.reset();
            }}
            className={styles.errorAlert}
          >
            {startChatMutation.error?.message || "An error occurred"}
          </Alert>
        )}
        {/* Conditional Views */}
        <div className={styles.chatView}>
          {chat ? (
            <ChatByIdView
              chat={chat}
              isCancelling={isCancelling}
              onRetry={handleRetry}
            />
          ) : (
            <NewChatView onExampleClick={handleExampleClick} />
          )}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <ChatInputBox
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={sending || isGenerating}
          isCancelling={isCancelling}
        />
      </div>
    </>
  );
};
